import { getDefaultTokens } from '@src/configs/defaults'
import { DEFAULT_BUY_TOKEN, DEFAULT_SELL_TOKEN } from '@src/constants/defaultTokens'
import { IExchangeData } from '@src/types/exchange'
import { IToken } from '@src/types/token'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('tokenValidation')

/**
 * Checks if two tokens are the same (same address on same chain)
 */
export function isSameToken(token1?: IToken, chainId1?: number, token2?: IToken, chainId2?: number): boolean {
    if (!token1 || !token2) return false

    const effectiveChainId1 = chainId1 ?? token1.chainId
    const effectiveChainId2 = chainId2 ?? token2.chainId

    return token1.address.toLowerCase() === token2.address.toLowerCase() && effectiveChainId1 === effectiveChainId2
}

/**
 * Checks if two exchange data objects have the same token (same address on same chain)
 */
export function isSameExchangeToken(data1: IExchangeData, data2: IExchangeData): boolean {
    return (
        data1.token &&
        data2.token &&
        data1.token.address.toLowerCase() === data2.token.address.toLowerCase() &&
        data1.chainId === data2.chainId
    )
}

/**
 * Validates URL parameters to prevent same token on both sides
 * @param params URL search parameters
 * @param updateBothData Function to update both sell and buy data
 * @param setSellAmountString Optional function to update the sell amount string in the UI
 * @returns true if parameters were valid, false if they were reset
 */
export function validateUrlTokenParams(
    params: URLSearchParams,
    updateBothData: (data: { sell: Partial<IExchangeData>; buy: Partial<IExchangeData> }) => void,
    setSellAmountString?: (value: string) => void
): boolean {
    const sellChain = params.get('sellChain')
    const buyChain = params.get('buyChain')
    const sellToken = params.get('sellToken')
    const buyToken = params.get('buyToken')
    const sellAmount = params.get('sellAmount')
    const sellDecimals = params.get('sellDecimals')

    // Check for same token on same chain
    if (sellChain && buyChain && sellToken && buyToken) {
        if (sellChain === buyChain && sellToken.toLowerCase() === buyToken.toLowerCase()) {
            logger.info('URL parameters contain same token on same chain - resetting to defaults')

            // Get dynamic default tokens
            const { sellToken: defaultSellToken, buyToken: defaultBuyToken } = getDefaultTokens()

            // Reset to default tokens
            updateBothData({
                sell: {
                    token: defaultSellToken,
                    chainId: defaultSellToken.chainId,
                    value: BigInt(0),
                },
                buy: {
                    token: defaultBuyToken,
                    chainId: defaultBuyToken.chainId,
                    value: BigInt(0),
                },
            })

            return false
        }

        // Process sellAmount if present
        if (sellAmount) {
            try {
                const sellAmountBigInt = BigInt(sellAmount)
                logger.info('URL contains sellAmount parameter:', sellAmount)

                // Update the sell data with the amount from URL
                updateBothData({
                    sell: {
                        value: sellAmountBigInt,
                    },
                    buy: {}, // No changes to buy data
                })

                // Also update the sellAmountString if the function is provided
                if (setSellAmountString) {
                    try {
                        // If we have decimals, format the amount properly
                        if (sellDecimals) {
                            const decimals = parseInt(sellDecimals, 10)
                            // Convert from raw amount to human-readable format
                            const numericAmount = Number(sellAmount) / 10 ** decimals

                            // Format with appropriate precision
                            const formattedAmount = numericAmount.toString()
                            logger.info('Setting formatted sellAmountString from URL parameter with decimals:', {
                                rawAmount: sellAmount,
                                decimals,
                                formattedAmount,
                            })
                            setSellAmountString(formattedAmount)
                        } else {
                            // No decimals provided, use raw amount
                            logger.info('Setting raw sellAmountString from URL parameter:', sellAmount)
                            setSellAmountString(sellAmount)
                        }
                    } catch (error) {
                        // If there's an error with formatting, fall back to the original string
                        logger.error('Error formatting sell amount string:', error)
                        setSellAmountString(sellAmount)
                    }
                }
            } catch (error) {
                logger.error('Failed to parse sellAmount from URL:', error)
            }
        }
    }

    return true
}

/**
 * Validates if selectedTokens already exist in localStorage or are null,
 * before saving to localStorage.
 */
export function areSelectedTokensValid(sellToken: IToken, buyToken: IToken): boolean {
    return sellToken !== null && buyToken !== null
}

/**
 * Helper function to calculate adjusted value when decimals change
 */
function calculateAdjustedValue(value: bigint, fromDecimals: number, toDecimals: number): bigint {
    if (fromDecimals === toDecimals) {
        return value
    }

    // Handle the case where the decimals are different
    if (fromDecimals > toDecimals) {
        // Need to reduce precision, shift right
        return value / BigInt(10 ** (fromDecimals - toDecimals))
    } else {
        // Need to increase precision, shift left
        return value * BigInt(10 ** (toDecimals - fromDecimals))
    }
}

/**
 * Handles token selection for buy/sell sides with automatic handling of duplicate prevention
 */
export function handleTokenSelection(
    newData: { token?: IToken; chainId?: number },
    type: 'buy' | 'sell',
    sellData: IExchangeData,
    buyData: IExchangeData,
    updateBothData: (data: { sell: Partial<IExchangeData>; buy: Partial<IExchangeData> }) => void
): void {
    if (!newData.token) return

    // Get dynamic default tokens
    const defaultTokens = getDefaultTokens()
    const defaultSellToken = defaultTokens.sellToken
    const defaultBuyToken = defaultTokens.buyToken

    if (type === 'sell') {
        const sellAdjustedValue = calculateAdjustedValue(
            sellData.value,
            sellData.token.decimals,
            newData.token.decimals
        )

        if (isSameToken(newData.token, newData.chainId, buyData.token, buyData.chainId)) {
            // If user selected defaultBuyToken, use defaultSellToken for buy side
            // If user selected defaultSellToken, use defaultBuyToken for buy side
            const oppositeToken = isSameToken(newData.token, newData.chainId, defaultBuyToken, defaultBuyToken.chainId)
                ? defaultSellToken
                : isSameToken(newData.token, newData.chainId, defaultSellToken, defaultSellToken.chainId)
                  ? defaultBuyToken
                  : defaultBuyToken // fallback for non-default tokens

            updateBothData({
                sell: {
                    token: newData.token,
                    chainId: newData.token.chainId,
                    value: sellAdjustedValue,
                },
                buy: {
                    token: oppositeToken,
                    chainId: oppositeToken.chainId,
                    value: buyData.value,
                },
            })
        } else {
            // Normal update - only update sell side
            updateBothData({
                sell: {
                    token: newData.token,
                    chainId: newData.token.chainId,
                    value: sellAdjustedValue,
                },
                buy: buyData, // Keep existing buy data
            })
        }
    } else {
        // Handle buy side selection
        const buyAdjustedValue = calculateAdjustedValue(buyData.value, buyData.token.decimals, newData.token.decimals)

        if (isSameToken(newData.token, newData.chainId, sellData.token, sellData.chainId)) {
            // If user selected defaultSellToken, use defaultBuyToken for sell side
            // If user selected defaultBuyToken, use defaultSellToken for sell side
            const oppositeToken = isSameToken(
                newData.token,
                newData.chainId,
                defaultSellToken,
                defaultSellToken.chainId
            )
                ? defaultBuyToken
                : isSameToken(newData.token, newData.chainId, defaultBuyToken, defaultBuyToken.chainId)
                  ? defaultSellToken
                  : defaultSellToken // fallback for non-default tokens

            updateBothData({
                buy: {
                    token: newData.token,
                    chainId: newData.token.chainId,
                    value: buyAdjustedValue,
                },
                sell: {
                    token: oppositeToken,
                    chainId: oppositeToken.chainId,
                    value: sellData.value,
                },
            })
        } else {
            // Normal update - only update buy side
            updateBothData({
                buy: {
                    token: newData.token,
                    chainId: newData.token.chainId,
                    value: buyAdjustedValue,
                },
                sell: sellData, // Keep existing sell data
            })
        }
    }
}

/**
 * Validates that default tokens don't create duplicates and handles this case
 * @returns The appropriate tokens to use that won't create duplicates
 */
export function validateDefaultTokens(
    sellData: IExchangeData,
    buyData: IExchangeData
): { sellToken: IToken; buyToken: IToken } {
    // Get dynamic default tokens
    const defaultTokens = getDefaultTokens()
    const defaultSellToken = defaultTokens.sellToken
    const defaultBuyToken = defaultTokens.buyToken

    // Check if resetting to defaults would create a duplicate
    if (
        defaultSellToken.chainId === defaultBuyToken.chainId &&
        defaultSellToken.address.toLowerCase() === defaultBuyToken.address.toLowerCase()
    ) {
        logger.info('Default tokens would create a duplicate - providing alternative defaults')

        // If the current sell token is already the defaultSellToken,
        // then we need to find an alternative for buy token
        if (
            sellData.token?.address.toLowerCase() === defaultSellToken.address.toLowerCase() &&
            sellData.chainId === defaultSellToken.chainId
        ) {
            // Return sell token as is and find alternative buy token
            // This could be a predefined alternative or the current buy token if it's different
            const alternativeBuyToken = isSameToken(
                buyData.token,
                buyData.chainId,
                defaultSellToken,
                defaultSellToken.chainId
            )
                ? DEFAULT_BUY_TOKEN // Use static default as fallback
                : buyData.token

            return {
                sellToken: defaultSellToken,
                buyToken: alternativeBuyToken,
            }
        } else {
            // Otherwise, keep buy as defaultBuyToken and find alternative for sell
            return {
                sellToken: DEFAULT_SELL_TOKEN, // Use static default as fallback
                buyToken: defaultBuyToken,
            }
        }
    }

    // No duplicate issue, return defaults
    return {
        sellToken: defaultSellToken,
        buyToken: defaultBuyToken,
    }
}
